<template>
  <div class="progress-bar">
    <div class="progress-bar__content">
      <span v-if="title" class="progress-bar__title">{{ title }}</span>
      <span class="progress-bar__value">{{ valueText }}</span>
    </div>
    <div class="progress-bar__track" :class="`progress-bar__track--${colorState}`">
      <div class="progress-bar__fill" :style="{ width: `${percentage}%` }" />
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  current: number;
  min?: number;
  max?: number;
  title?: string;
  warningThreshold?: number;
  dangerThreshold?: number;
}

const props = withDefaults(defineProps<Props>(), {
  min: 0,
  max: 100,
  title: '',
  warningThreshold: 50,
  dangerThreshold: 25
});

const percentage = computed(() => {
  const range = props.max - props.min;
  const value = props.current - props.min;
  return Math.min(Math.max((value / range) * 100, 0), 100);
});

const colorState = computed(() => {
  if (percentage.value <= props.dangerThreshold) return 'danger';
  if (percentage.value <= props.warningThreshold) return 'warning';
  return 'success';
});

const valueText = computed(() => `${Math.floor(props.current)}/${Math.floor(props.max)}`);
</script>

<style lang="scss" scoped>
.progress-bar {
  width: 100%;
  min-width: 198px;
  max-width: 370px;
  height: 32px;
  position: relative;

  &__content {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    z-index: 2;
    pointer-events: none;
    gap: 8px;

    font-family: Eczar;
    font-weight: 400;
    font-size: 16px;
    line-height: 1;
    color: #ffffff;
    -webkit-text-stroke: 2px #000000;
    text-stroke: 2px #000000;
    paint-order: stroke fill;
  }

  &__title {
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 0 1 auto;
  }

  &__value {
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__track {
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, #17343e 0%, #010303 100%);
    border-radius: 8px;
    position: relative;
    border: 1px solid transparent;
    transition: border-color 0.3s ease;

    &--success {
      border-color: #01c0fe;
    }

    &--warning {
      border-color: #fea101;
    }

    &--danger {
      border-color: #b72114;
    }
  }

  &__fill {
    height: 100%;
    border-radius: 6px;
    transition:
      width 0.4s ease,
      background-color 0.3s ease;
    position: relative;

    .progress-bar__track--success & {
      background-color: #01c0fecc;
    }

    .progress-bar__track--warning & {
      background-color: #fea101cc;
    }

    .progress-bar__track--danger & {
      background-color: #b72114cc;
    }
  }
}
</style>
